// ODS-EWO-033 P1. One way to move between applications, driven by the
// catalogue and the organization's entitlements. It navigates and it pins; it
// grants nothing, and an application it lists is still admitted by its own
// route guard.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@odingard/design-system";
import type { Shell, ShellApplication } from "./useShell";

function offerLabel(app: ShellApplication): string | null {
  switch (app.offer) {
    case "trial":
      return "Start a trial";
    case "subscribe":
      return "Subscribe";
    case "coming_soon":
      return "Coming soon";
    default:
      return null;
  }
}

export function AppSwitcher({ shell }: { shell: Shell }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(0);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const wasOpen = useRef(false);

  const current = shell.current;
  const currentApp = shell.applications.find((a) => a.manifest.id === current?.id);
  // Everything the customer holds first, then what they do not, so the list is
  // ordered by what it can actually do rather than alphabetically.
  const items = [...shell.entered, ...shell.applications.filter((a) => !a.canEnter)];

  useEffect(() => {
    if (!open) {
      // Closing returns the keyboard to the control that opened the menu, and
      // only when the menu is what closed (ODS-UX-001 §9).
      if (wasOpen.current) trigger.current?.focus();
      wasOpen.current = false;
      return;
    }
    wasOpen.current = true;
    itemRefs.current[focused]?.focus();
  }, [open, focused]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  function enter(app: ShellApplication) {
    setOpen(false);
    // An unknown entitlement is not a refusal. While the platform has not
    // answered, the shell navigates and the application's own guard admits or
    // refuses; sending an entitled customer to a sales page because the shell
    // was still loading would be a worse error than a guard's refusal.
    if (app.canEnter || !shell.entitlementsKnown) {
      shell.recordOpen(app.manifest.catalogSlug);
      navigate(app.manifest.primaryRoute);
      return;
    }
    // Not entitled: the marketplace is where availability, price and the trial
    // offer are decided, so the shell hands the customer there rather than
    // reproducing the offer itself.
    navigate(`/marketplace/apps/${app.manifest.catalogSlug}`);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocused((i) => (i + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocused((i) => (i - 1 + items.length) % items.length);
    } else if (event.key === "Home") {
      event.preventDefault();
      setFocused(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setFocused(items.length - 1);
    }
  }

  return (
    <div className="app-switcher" ref={container}>
      <button
        type="button"
        ref={trigger}
        className="app-switcher__trigger ods-btn ods-btn--ghost ods-btn--sm"
        aria-haspopup="menu"
        aria-expanded={open}
        /* The name says which application you are in: the icon and the rail's
           heading are visual, and a screen reader gets neither. */
        aria-label={
          currentApp
            ? `Switch application. You are in ${currentApp.name}.`
            : "Switch application. You are in Studio."
        }
        onKeyDown={onKeyDown}
        onClick={() => {
          setFocused(0);
          setOpen((o) => !o);
        }}
      >
        <span aria-hidden="true">{currentApp?.icon ?? "◇"}</span>
        <span className="app-switcher__name">{currentApp?.name ?? "Studio"}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        /* The notice and the empty message sit outside the menu, because a menu
           may only contain menu items: a paragraph inside it makes the whole
           menu invalid to a screen reader (ODS-EWO-033 P6). */
        <div className="app-switcher__menu">
          {shell.reach !== "complete" && (
            <p className="app-switcher__notice" role="status">
              {shell.reach === "entitlements_only"
                ? "The catalogue is unavailable, so trials and prices are missing here. The applications you hold still open."
                : shell.reach === "catalog_only"
                  ? "Your entitlements could not be read, so this list shows what Studio sells rather than what you hold. Each application still checks for itself."
                  : "Studio cannot reach the catalogue or your entitlements. Navigation works; what you hold is unknown until they return."}
            </p>
          )}
          <div role="menu" aria-label="Switch application">
            {items.map((app, index) => {
              const label = offerLabel(app);
              const isCurrent = app.manifest.id === current?.id;
              return (
                <div className="app-switcher__row" role="none" key={app.manifest.id}>
                  <button
                    type="button"
                    role="menuitem"
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    tabIndex={index === focused ? 0 : -1}
                    onKeyDown={onKeyDown}
                    className={`app-switcher__item ${isCurrent ? "app-switcher__item--current" : ""}`}
                    aria-current={isCurrent ? "true" : undefined}
                    onClick={() => enter(app)}
                  >
                    <span aria-hidden="true">{app.icon}</span>
                    <span className="app-switcher__item-name">{app.name}</span>
                    {app.manifest.surfaceOf && <Badge tone="neutral">Surface</Badge>}
                    {app.customerState?.state === "trialing" && <Badge tone="info">Trial</Badge>}
                    {label && <Badge tone="neutral">{label}</Badge>}
                  </button>
                  {app.canEnter && (
                    <button
                      type="button"
                      /* A pin is a toggle inside a menu, which is what
                         menuitemcheckbox is for; aria-pressed is not a state a
                         menu item may carry. */
                      role="menuitemcheckbox"
                      tabIndex={-1}
                      className="app-switcher__pin"
                      aria-label={app.pinned ? `Unpin ${app.name}` : `Pin ${app.name}`}
                      aria-checked={app.pinned}
                      onKeyDown={onKeyDown}
                      onClick={() => void shell.setPinned(app.manifest.catalogSlug, !app.pinned)}
                    >
                      <span aria-hidden="true">{app.pinned ? "★" : "☆"}</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {items.length === 0 && (
            <p className="app-switcher__notice">No applications to show yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
